import React, { useMemo } from 'react';

import Conditional from '../../../common/conditional';
import ConcreteHammerData, { DataRange } from '../concrete-hammer-summary/data';
import CustomSection from '../../custom-general/custom-section';
import css from './style.module.scss';

import { ConcreteHammerReport, ConcreteHammerReportItem } from '../../../../utils/models';
import { concatClassNames, formatTime, getConcreteDataRange } from '../../../../utils/utilities';
import { APP_DATE_FORMAT } from '../../../../utils/constants';

interface Props {
    startRange: number;
    endRange: number;
    overview: ConcreteHammerReport,
    data: ConcreteHammerReportItem[];
	sectionTitle: string;
	sectionSubtitle?: string;
	shouldDisplayOverview: boolean;
	shouldDisplaySummary: boolean;
}

const ConcreteHammerReportPDF: React.FC<Props> = ({ overview, data, startRange, endRange, shouldDisplayOverview, shouldDisplaySummary, sectionTitle, sectionSubtitle }) => {
	const rangesToRender: DataRange[] = useMemo(() => {
		return getConcreteDataRange(startRange, endRange);
	}, [startRange, endRange]);

    return ( 
		<>
			{rangesToRender.map((rangeToRender, index) => (
				<CustomSection key={rangeToRender.startRange} sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle}>
					<div className={css.concreteHammerReport}>
						<Conditional isVisible={shouldDisplayOverview}>
							<div className="container-fluid overflow-hidden">
								<div className="row">
									<div className="col">
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Test Date
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.testDate ? formatTime(overview.testDate, APP_DATE_FORMAT) : 'N/A'}
											</div>
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Surface Condition
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.surfaceCondition}
											</div>
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Stage of test
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.stageOfTest}
											</div>
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Index
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.index}
											</div>
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Hammer Orientation
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.hammerOrientation}
											</div>
										</div>
									</div>
		
									<div className="col">
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Equipment and Modal
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.equipment}
											</div>
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Serial Number
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.serialNumber}
											</div>
										</div>
		
										<div className="col">
											<div className="row">
												<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
													Calibration Due Date
												</div>
												<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
													{overview.calibrationDueDate ? formatTime(overview.calibrationDueDate, APP_DATE_FORMAT) : 'N/A'}
												</div>
											</div>		
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Angle (deg)
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.angle}
											</div>
										</div>
		
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Face
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.face}
											</div>
										</div>
									</div>
								</div>
							</div>
		
							<div className={css.concreteHammerReportSection}></div>
		
							<div className="container-fluid overflow-hidden">
								<div className="row">
									<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
										Ref Std.
									</div>
									<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
										{overview.refStd}
									</div>
								</div>
		
								<div className="row">
									<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
										Acceptance Std.
									</div>
									<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
										{overview.acceptanceStd}
									</div>
								</div>
		
								<div className="row">
									<div className="col">
										<div className="row">
											<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
												Material
											</div>
											<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
												{overview.material}
											</div>
										</div>
									</div>
								</div>
							</div>
		
							<div className={css.concreteHammerReportSection}></div>
						</Conditional>
		
						<ConcreteHammerData data={data} dataRange={rangeToRender} showTotal={index === 0} />
		
						<Conditional isVisible={shouldDisplaySummary}>
							<div className={css.concreteHammerReportSection}></div>
		
							<div className="container-fluid overflow-hidden">
								<div className="row">
									<div className={concatClassNames([css.concreteHammerReportHeader, 'col-4'])}>
										Summary
									</div>
									<div className={concatClassNames([css.concreteHammerReportBody, 'col'])}>
										{overview.summary}
									</div>
								</div>
							</div>
						</Conditional>
					</div>
				</CustomSection>
			))}
		</>
     );
};
 
export default ConcreteHammerReportPDF;